<template>
	<div id="initial-decision-details">
		<div v-if="selectedDecision.label !== undefined">
			<back-nav-btn :action="backToInitialDecisionPool">Back to Initial Decision Pool</back-nav-btn>
			<editActionBar
				:isEditActive="update"
				:updateText="'Edit ' + selectedDecision.label"
				:confirmAction="onSave" 
				:updateAction="onEditInitialDecision" 
				:cancelAction="onCancel" 
				:deleteAllowed="false"
				:disableWithoutDelete="true"
				:enableAction="onDisableEnable" 
				:disableAction="onDisableEnable" 
				:isDisabled="!selectedDecision.isActive"
			/>

			<div class="property-settings">
				<h3 class="line-header">Property Settings</h3>
				<v-form id="property-info" class="property-info" :key="update" v-model="isDecisionValid">
					<div class="property-identification">
						<fieldset>
							<v-text-field
								v-model="selectedDecision.initialDecisionConfigId"
								label="Property Name"
								:readonly="true"
								:rules="rules"
							/>
							<v-text-field
								v-model="selectedDecision.label"
								label="Display Label"
								:readonly="!update"
								:outlined="update"
								:rules="rules"
							/>
						</fieldset>
					</div>
					<div class="property-operator-options">
						<fieldset>
							<label class="available-operations-label">Available Operations</label>
							<div 
								v-for="op in selectedDecision.operatorOptions"
								:key="op.operator"
							>
								<input
									type="checkbox"
									:disabled="!update"
									:id="op.operator"
									:name="op.operator"
									:value="op.isActive"
									:checked="op.isActive"
									@change="updateOperators"
									class="operator-checkbox"
								/>
								<label :for="op.operator" class="operator-label">{{ op.label }}</label>
							</div>
						</fieldset>
					</div>
					<div v-if="selectedDecision.operatorOptions.find(op => op.operator === 'Equal').isActive ||
						selectedDecision.operatorOptions.find(op => op.operator === 'NotEqual').isActive"
						class="property-value-options">
						<fieldset>
							<label class="value-options-label">Value Options</label>
							<div 
								v-for="op in selectedDecision.rightSideOptions"
								:key="op.optionId"
							>
								<input
									type="checkbox"
									:disabled="!update"
									:id="op.optionId"
									:name="op.optionId"
									:value="op.isActive"
									:checked="op.isActive"
									@change="updateValues"
									class="values-checkbox"
								/>
								<label :for="op.operator" class="value-label">{{ op.label }}</label>
							</div>
						</fieldset>
					</div>
				</v-form>
			</div>
			<div v-if="valueOption.isActive" class="value-settings">
				<h3 class="line-header">Value Settings</h3>
				<v-form id="value-info" class="value-info" :key="update" v-model="isDecisionValid">
					<div class="value-properties">
						<fieldset>
							<v-text-field
								v-model="valueOption.label"
								label="Label"
								:readonly="!update"
								:outlined="update"
								:rules="rules"
							/>
							<v-text-field
								v-model="valueOption.validateRegex"
								label="Regex for Validation"
								:readonly="!update"
								:outlined="update"
								:rules="rules"
							/>
						</fieldset>
					</div>
					<div class="more-value-properties">
						<fieldset>
							<v-text-field
								v-model="valueOption.placeholder"
								label="Helpful Placeholder"
								:readonly="!update"
								:outlined="update"
								:rules="rules"
							/>
							<label class="value-options-label">Value Type</label>
							<v-radio-group
								v-model="dataType"
								mandatory
								class="data-type-radio"
							>
								<div 
									v-for="type in dataTypes"
									:key="type.id"
								>
									<v-radio
										:disabled="!update"
										:label="type.label"
										:value="type.id"
									>
									</v-radio>
								</div>
							</v-radio-group>
						</fieldset>
					</div>
				</v-form>
			</div>
		</div>
		<v-skeleton-loader v-else type="article"></v-skeleton-loader>
	</div>
</template>

<script>
	/**
	 * @fileoverview initial-decision-details.vue displays the meta data for the selected initial decision.
	 * It relies on vuex to map the state variables from the store files.
	 * 
	 * @components
	 * 		editActionBar - action bar that allows the user to edit, save changes, cancel changes, enable and disable decisions
	 * 
	 * @author Sophia Frankel <sophia.frankel@quavo.com>
	 */
	import { mapState } from 'vuex';
	import editActionBar from '@/components/edit-action-bar.vue';

	export default {
		data() {
			return {
				update: false,	// updating in progress
				isDecisionValid: false,	// validation flag
				/**
				 * Validation rules for data fields
				 */
				rules: [
					value => !!value || 'Required',
					value => (value || '').length > 0 || 'Required',
				],
				tempDecision: {},	// temporary decision for restoration	
				dataTypes: [{label: "Number", id: "number"}, {label: "Text", id: "text"}],	// data type options
			}
		},
		props: {
			/**
			 * Selected initial decision
			 */
			decision: {
				type: Object
			}
		},
		components: {
			editActionBar
		},
		computed: {
			...mapState({
				activeTenantId: (state) => state.account.activeTenantId,
			}),
			/**
			 * Getter and setter for the selected initial decision
			 */
			selectedDecision: {
				get() {
					return this.decision;
				},
				set(decision) {
					this.selectedDecision.tenantId = decision.tenantId;
					this.selectedDecision.rightSideType = decision.rightSideType;
					this.selectedDecision.leftSideValue = decision.leftSideValue;
					this.selectedDecision.operatorOptions = decision.operatorOptions;
					this.selectedDecision.rightSideOptions = decision.rightSideOptions;
					this.selectedDecision.leftSideType = decision.leftSideType;
					this.selectedDecision.initialDecisionConfigId = decision.initialDecisionConfigId;
					this.selectedDecision.label = decision.label;
					this.selectedDecision.isActive = decision.isActive;
				}
			},
			/**
			 * Getter and setter for the data type for the initial decision value
			 */
			dataType: {
				get() {
					return this.valueOption.dataType;
				},
				set(val) {
					this.valueOption.dataType = val;
				}
			},
			/**
			 * Right side value option
			 * @returns the right side value option
			 */
			valueOption() {
				return this.selectedDecision.rightSideOptions.find(op => op.optionId === 'value');
			}
		},
		methods: {
			/**
			 * Back to initial decision pool
			 */
			backToInitialDecisionPool() {
				this.$emit('backToInitialDecisionPool');
			},
			/**
			 * Save initial decision changes
			 */
			onSave(){
				if (this.isDecisionValid) {
					this.selectedDecision.tenantId = this.activeTenantId;
					this.$store.dispatch("updateInitialDecisionMetaData", this.selectedDecision);
				} else {
					this.$store.dispatch("notifications/addNotification", {
						status: 'error',
						message: 'Fix missing/invalid information and try again.',
					});
				}

				this.update = false;
			},
			/**
			 * Update checkbox values for operators
			 * @param {Object} event - change event triggering an update of the checkboxes
			 */
			updateOperators(event) {
				let op = this.selectedDecision.operatorOptions.find(op => op.operator === event.srcElement.id);
				op.isActive = !op.isActive;
			},
			/**
			 * Update checkbox values for values
			 * @param {Object} - change event triggering an update of the checkboxes
			 */
			updateValues(event) {
				let op = this.selectedDecision.rightSideOptions.find(op => op.optionId === event.srcElement.id);
				op.isActive = !op.isActive;
			},
			/**
			 * Cancel changes, restore original decision data
			 */
			onCancel() {
				this.update = false;

				// restore selectedDecision from backup... then cleanup
				this.selectedDecision = this.tempDecision;
				this.tempDecision = {};
			},
			/**
			 * Edit initial decision. Clone the initial decison for restoration
			 */
			onEditInitialDecision() {
				this.update = true;

				// make deep clone of inital decision for restoration
				this.tempDecision = this._.cloneDeep(this.decision);
			},
			/**
			 * Enable/Disable initial decision
			 */
			onDisableEnable() {
				this.selectedDecision.tenantId = this.activeTenantId;
				this.selectedDecision.isActive = !this.selectedDecision.isActive;
				this.$store.dispatch("updateInitialDecisionMetaData", this.selectedDecision);
			}
		}
	}
</script>

<style>

</style>